import React, { useState, useEffect } from 'react';
import { MODULES } from '../utils/permissions';

const CreateStaff = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [adminId, setAdminId] = useState('');
  const [adminName, setAdminName] = useState('');
  
  const availableModels = MODULES;
  const availablePaths = Object.fromEntries(
    MODULES.map(({ module, paths }) => [module, paths]),
  );
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    isActive: true,
    permissions: [],
    profileImage: null
  });

  // Get admin info from localStorage on component mount
  useEffect(() => {
    const storedAdminId = localStorage.getItem('adminId');
    const storedAdminName = localStorage.getItem('name');
    
    if (storedAdminId) {
      setAdminId(storedAdminId);
    }
    
    if (storedAdminName) {
      setAdminName(storedAdminName);
    }
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear previous errors when user starts typing
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  // Handle file upload
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        profileImage: e.target.files[0]
      }));
    }
  };

  // Handle Permission changes
  const handlePermissionChange = (moduleIndex, pathIndex, field, value) => {
    setFormData(prev => {
      const newPermissions = [...prev.permissions];
      
      // If module is changed, update the paths dropdown options
      if (field === 'module') {
        newPermissions[moduleIndex].module = value;
        // Reset paths when module changes
        newPermissions[moduleIndex].paths = [{ path: '', accessLevel: 'view' }];
      } else if (field === 'path') {
        newPermissions[moduleIndex].paths[pathIndex].path = value;
      } else if (field === 'accessLevel') {
        newPermissions[moduleIndex].paths[pathIndex].accessLevel = value;
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  // Get available paths for a specific module
  const getPathsForModule = (moduleName) => {
    if (!moduleName) return [];
    return availablePaths[moduleName] || [];
  };

  // Add new permission module
  const addPermissionModule = () => {
    setFormData(prev => ({
      ...prev,
      permissions: [
        ...prev.permissions,
        { module: '', paths: [{ path: '', accessLevel: 'view' }] }
      ]
    }));
  };

  // Add new path to permission
  const addPermissionPath = (moduleIndex) => {
    setFormData(prev => {
      const newPermissions = [...prev.permissions];
      newPermissions[moduleIndex].paths.push({ path: '', accessLevel: 'view' });
      return { ...prev, permissions: newPermissions };
    });
  };

  // Remove permission module
  const removePermissionModule = (moduleIndex) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.filter((_, idx) => idx !== moduleIndex)
    }));
  };

  // Remove permission path
  const removePermissionPath = (moduleIndex, pathIndex) => {
    setFormData(prev => {
      const newPermissions = [...prev.permissions];
      newPermissions[moduleIndex].paths = newPermissions[moduleIndex].paths.filter(
        (_, idx) => idx !== pathIndex
      );
      return { ...prev, permissions: newPermissions };
    });
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate required fields
    if (!formData.name || !formData.email || !formData.password) {
      setError('Name, Email, and Password are required fields');
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Validate phone if provided
    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      setError('Phone number must be 10 digits');
      setLoading(false);
      return;
    }

    // Check if admin is logged in
    if (!adminId) {
      setError('You must be logged in as an admin to create staff members');
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('isActive', formData.isActive);
      formDataToSend.append('createdBy', adminId);
      
      // Append permissions as JSON string
      if (formData.permissions.length > 0) {
        formDataToSend.append('permissions', JSON.stringify(formData.permissions));
      }
      
      // Append profile image if selected
      if (formData.profileImage) {
        formDataToSend.append('profileImage', formData.profileImage);
      }

      const response = await fetch('https://api.editezy.com/api/staff/createstaff', {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        // Reset form (keep admin info)
        setFormData({
          name: '',
          email: '',
          phone: '',
          password: '',
          isActive: true,
          permissions: [],
          profileImage: null
        });
        // Reset file input
        const fileInput = document.getElementById('profileImage');
        if (fileInput) fileInput.value = '';
      } else {
        setError(data.message || 'Failed to create staff member');
      }
    } catch (err) {
      setError('Error creating staff: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      isActive: true,
      permissions: [],
      profileImage: null
    });
    const fileInput = document.getElementById('profileImage');
    if (fileInput) fileInput.value = '';
    setError(null);
    setSuccess(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Create Staff Member</h1>
          <p style={styles.subtitle}>
            Add a new staff member with permissions
            {adminName && (
              <span style={styles.adminInfo}> — Creating as: <strong>{adminName}</strong></span>
            )}
          </p>
          {!adminId && (
            <p style={styles.warningText}>
              ⚠️ You must be logged in as an admin to create staff members
            </p>
          )}
        </div>

        {/* Success Message */}
        {success && (
          <div style={styles.successAlert}>
            <span style={styles.alertIcon}>✅</span>
            <div>
              <strong>Success!</strong>
              <p style={styles.alertText}>Staff member created successfully!</p>
            </div>
            <button onClick={() => setSuccess(false)} style={styles.alertClose}>×</button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={styles.errorAlert}>
            <span style={styles.alertIcon}>⚠️</span>
            <div>
              <strong>Error!</strong>
              <p style={styles.alertText}>{error}</p>
            </div>
            <button onClick={() => setError(null)} style={styles.alertClose}>×</button>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Basic Information */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Basic Information</h2>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Full Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter full name"
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Email Address <span style={styles.required}>*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number (10 digits)"
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Password <span style={styles.required}>*</span>
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password (min 6 characters)"
                style={styles.input}
                required
                minLength="6"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                Active Status
              </label>
            </div>
          </div>

          {/* Profile Image */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Profile Image</h2>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Upload Profile Photo
              </label>
              <input
                type="file"
                id="profileImage"
                name="profileImage"
                accept="image/*"
                onChange={handleFileChange}
                style={styles.fileInput}
              />
              {formData.profileImage && (
                <div style={styles.fileInfo}>
                  <span>📎 {formData.profileImage.name}</span>
                  <span style={styles.fileSize}>
                    ({(formData.profileImage.size / 1024).toFixed(2)} KB)
                  </span>
                </div>
              )}
              <p style={styles.helperText}>Supported formats: JPG, PNG, GIF (Max 5MB)</p>
            </div>
          </div>

          {/* Permissions */}
          <div style={styles.section}>
            <div style={styles.permissionsHeader}>
              <h2 style={styles.sectionTitle}>Permissions</h2>
              <button
                type="button"
                style={styles.addModuleBtn}
                onClick={addPermissionModule}
              >
                + Add Module
              </button>
            </div>

            {formData.permissions.length === 0 && (
              <div style={styles.emptyPermissions}>
                <span style={styles.emptyIcon}>🔒</span>
                <p>No permissions added yet. Click "Add Module" to get started.</p>
              </div>
            )}

            {formData.permissions.map((perm, moduleIndex) => (
              <div key={moduleIndex} style={styles.permissionCard}>
                <div style={styles.permissionHeader}>
                  <select
                    value={perm.module}
                    onChange={(e) => handlePermissionChange(moduleIndex, 0, 'module', e.target.value)}
                    style={styles.moduleSelect}
                  >
                    <option value="">Select Module</option>
                    {availableModels.map((model) => (
                      <option key={model.module} value={model.module}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    style={styles.removeModuleBtn}
                    onClick={() => removePermissionModule(moduleIndex)}
                  >
                    × Remove Module
                  </button>
                </div>

                {perm.paths.map((path, pathIndex) => (
                  <div key={pathIndex} style={styles.pathRow}>
                    <select
                      value={path.path}
                      onChange={(e) => handlePermissionChange(moduleIndex, pathIndex, 'path', e.target.value)}
                      style={styles.pathSelect}
                      disabled={!perm.module}
                    >
                      <option value="">
                        {perm.module ? 'Select Path' : 'Select a module first'}
                      </option>
                      {perm.module && getPathsForModule(perm.module).map((availablePath) => (
                        <option key={availablePath} value={availablePath}>
                          {availablePath}
                        </option>
                      ))}
                    </select>
                    <select
                      value={path.accessLevel}
                      onChange={(e) => handlePermissionChange(moduleIndex, pathIndex, 'accessLevel', e.target.value)}
                      style={styles.accessSelect}
                    >
                      <option value="view">View</option>
                      <option value="edit">Edit</option>
                      <option value="manage">Manage</option>
                      <option value="full">Full</option>
                    </select>
                    <button
                      type="button"
                      style={styles.removePathBtn}
                      onClick={() => removePermissionPath(moduleIndex, pathIndex)}
                    >
                      ×
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  style={styles.addPathBtn}
                  onClick={() => addPermissionPath(moduleIndex)}
                  disabled={!perm.module}
                >
                  + Add Path {!perm.module && '(Select module first)'}
                </button>
              </div>
            ))}
          </div>

          {/* Form Actions */}
          <div style={styles.formActions}>
            <button
              type="button"
              style={styles.resetBtn}
              onClick={resetForm}
            >
              Reset
            </button>
            <button
              type="submit"
              style={styles.submitBtn}
              disabled={loading || !adminId}
            >
              {loading ? (
                <>
                  <span style={styles.spinner}></span>
                  Creating...
                </>
              ) : (
                'Create Staff Member'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '2rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    background: '#f8fafc',
    minHeight: '100vh',
  },
  
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '2.5rem',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
  },
  
  header: {
    marginBottom: '2rem',
    borderBottom: '2px solid #e2e8f0',
    paddingBottom: '1.5rem',
  },
  
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
    marginBottom: '0.5rem',
  },
  
  subtitle: {
    fontSize: '1rem',
    color: '#64748b',
    margin: 0,
  },
  
  adminInfo: {
    color: '#3b82f6',
  },
  
  warningText: {
    fontSize: '0.95rem',
    color: '#dc2626',
    marginTop: '0.5rem',
    fontWeight: 500,
  },
  
  successAlert: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem',
    borderRadius: '8px',
    background: '#dcfce7',
    border: '1px solid #86efac',
    color: '#166534',
    marginBottom: '1.5rem',
  },
  
  errorAlert: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem',
    borderRadius: '8px',
    background: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    marginBottom: '1.5rem',
  },
  
  alertIcon: {
    fontSize: '1.5rem',
  },
  
  alertText: {
    margin: '0.25rem 0 0 0',
    fontSize: '0.9rem',
  },
  
  alertClose: {
    background: 'none',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    color: 'inherit',
    marginLeft: 'auto',
    padding: '0 0.25rem',
  },
  
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1e293b',
    margin: 0,
    marginBottom: '0.5rem',
  },
  
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  
  label: {
    fontWeight: 500,
    color: '#334155',
    fontSize: '0.9rem',
  },
  
  required: {
    color: '#dc2626',
  },
  
  input: {
    padding: '0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
    outline: 'none',
    background: 'white',
  },
  
  checkbox: {
    marginRight: '0.5rem',
    width: '18px',
    height: '18px',
    accentColor: '#3b82f6',
  },
  
  fileInput: {
    padding: '0.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.9rem',
    background: 'white',
  },
  
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem',
    background: '#f8fafc',
    borderRadius: '6px',
    fontSize: '0.9rem',
    color: '#334155',
  },
  
  fileSize: {
    color: '#94a3b8',
    fontSize: '0.8rem',
  },
  
  helperText: {
    fontSize: '0.85rem',
    color: '#94a3b8',
    margin: 0,
  },
  
  permissionsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  
  addModuleBtn: {
    padding: '0.5rem 1.25rem',
    border: 'none',
    borderRadius: '8px',
    background: '#3b82f6',
    color: 'white',
    fontWeight: 500,
    cursor: 'pointer',
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
  },
  
  emptyPermissions: {
    textAlign: 'center',
    padding: '2rem',
    border: '2px dashed #e2e8f0',
    borderRadius: '8px',
    color: '#94a3b8',
  },
  
  emptyIcon: {
    fontSize: '2.5rem',
    display: 'block',
    marginBottom: '0.5rem',
  },
  
  permissionCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '1.25rem',
    background: '#f8fafc',
  },
  
  permissionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  
  moduleSelect: {
    flex: 1,
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '0.95rem',
    minWidth: '150px',
    background: 'white',
    outline: 'none',
  },
  
  removeModuleBtn: {
    padding: '0.35rem 0.75rem',
    border: 'none',
    borderRadius: '4px',
    background: '#fef2f2',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '0.85rem',
    transition: 'all 0.2s ease',
  },
  
  pathRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
  },
  
  pathSelect: {
    flex: 2,
    padding: '0.4rem 0.6rem',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '0.9rem',
    minWidth: '120px',
    background: 'white',
    outline: 'none',
    cursor: 'pointer',
  },
  
  accessSelect: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '0.9rem',
    background: 'white',
    minWidth: '100px',
    outline: 'none',
    cursor: 'pointer',
  },
  
  removePathBtn: {
    padding: '0.2rem 0.5rem',
    border: 'none',
    borderRadius: '4px',
    background: '#fef2f2',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '0.8rem',
    transition: 'all 0.2s ease',
  },
  
  addPathBtn: {
    padding: '0.3rem 0.8rem',
    border: '1px dashed #94a3b8',
    borderRadius: '4px',
    background: 'transparent',
    color: '#475569',
    cursor: 'pointer',
    fontSize: '0.85rem',
    transition: 'all 0.2s ease',
    marginTop: '0.25rem',
  },
  
  formActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e2e8f0',
  },
  
  resetBtn: {
    padding: '0.75rem 1.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    background: 'white',
    color: '#64748b',
    fontWeight: 500,
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
  },
  
  submitBtn: {
    padding: '0.75rem 2rem',
    border: 'none',
    borderRadius: '8px',
    background: '#3b82f6',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    minWidth: '200px',
    justifyContent: 'center',
  },
  
  spinner: {
    display: 'inline-block',
    width: '18px',
    height: '18px',
    border: '3px solid rgba(255,255,255,0.3)',
    borderTop: '3px solid white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

// Add global styles
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus, select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  .add-module-btn:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .remove-module-btn:hover {
    background: #fee2e2;
  }
  
  .remove-path-btn:hover {
    background: #fee2e2;
  }
  
  .add-path-btn:hover:not(:disabled) {
    background: #f1f5f9;
  }
  
  .add-path-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .reset-btn:hover {
    background: #f1f5f9;
  }
  
  .submit-btn:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);
  }
  
  .submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .alert-close:hover {
    opacity: 0.7;
  }
  
  select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f1f5f9;
  }
`;
document.head.appendChild(styleSheet);

export default CreateStaff;